import isE164PhoneNumber from 'is-e164-phone-number'

export const sleep = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export const cleanUrl = (website: string) => {
  website = website?.trim()

  if (website?.includes('http://')) {
    website = website.replace(new URL(website).protocol, 'https:')
  }

  if (!website?.includes('http://') && !website?.includes('https://')) {
    const protocol = 'https://'
    website = protocol.concat(website)
  }

  return website
}

export const removeProtocol = (url) => {
  if (!url) {
    return ''
  }

  return url.replace(/(^\w+:|^)\/\//, '')
}

// truncate function decimal places without rounding (index=number decimals)
export const truncate = (number: number, index: number) => {
  // cutting the number
  return +number
    .toString()
    .slice(0, number.toString().indexOf('.') + (index + 1))
}

export const capitalize = (string) => {
  if (!string) {
    return ''
  }

  return string.charAt(0).toUpperCase() + string.slice(1)
}

export const formatLongMessage = (string) =>
  replaceURLs(string)?.replace(/\n/g, '<br />')

export const mergeRefs = (...refs: any) => {
  const filteredRefs = refs.filter(Boolean)
  if (!filteredRefs.length) {
    return null
  }
  if (filteredRefs.length === 0) {
    return filteredRefs[0]
  }

  return (inst: any) => {
    for (const ref of filteredRefs) {
      if (typeof ref === 'function') {
        ref(inst)
      } else if (ref) {
        ref.current = inst
      }
    }
  }
}

// https://www.cluemediator.com/find-urls-in-string-and-make-a-link-using-javascript
export const replaceURLs = (message) => {
  if (!message) return

  var urlRegex = /(((https?:\/\/)|(www\.))[^\s]+)/g
  return message.replace(urlRegex, function (url) {
    var hyperlink = url
    if (!hyperlink.match('^https?://')) {
      hyperlink = 'http://' + hyperlink
    }

    return (
      '<a href="' +
      hyperlink +
      '" target="_blank" rel="noopener noreferrer" title="' +
      url +
      '" class="text-blue-600 hover:text-blue-500 hover:underline">' +
      (url.length > 40 ? url.substring(0, 40) + '...' : url) +
      '</a>'
    )
  })
}

/**
 * compares two arrays and detects if its a deletion or an addition
 */
export const compareArrays = (
  oldArr: any[],
  newArr: any[],
): {
  type: 'addition' | 'deletion'
  item: any
} => {
  // Create a copy of the old array to modify
  const arr = [...oldArr]

  // Check for new additions
  for (const newItem of newArr) {
    const index = arr.findIndex((item) => item.value === newItem.value)

    if (index === -1) {
      return { type: 'addition', item: newItem }
    }

    arr.splice(index, 1)
  }

  // Check for deletions
  if (arr.length > 0) {
    return { type: 'deletion', item: arr[0] }
  }

  // If no changes detected, return null
  return null
}

export const findErrorMethodName = (e, errorCode) => {
  for (const error of e.graphQLErrors) {
    switch (error.code) {
      case errorCode: {
        return true
      }
    }
  }
  return false
}

export const validateE164PhoneNumber = (phoneNumber: string): boolean => {
  return isE164PhoneNumber(phoneNumber)
}

export const formatPhoneNumber = (phoneNumber: string): string => {
  return phoneNumber.replace(/[^\d+]/g, '')
}

export const extractBrandId = (url: string): string | null => {
  const regex = /\/b\/(\d+)\//
  const match = url.match(regex)
  return match ? match[1] : null
}

/**
 * Extracts the UUID part from a filename
 * @param fileName - Filename like "campaign-requirements-attachments/campaign-3407/a3dfa65a-aeb7-4616-89af-b724475a1059.pdf"
 * @returns Just the UUID part at the end (e.g., "a3dfa65a-aeb7-4616-89af-b724475a1059.pdf")
 */
export const extractUuidFromFileName = (fileName: string): string => {
  if (!fileName) return ''

  // Extract the part after the last "/"
  const parts = fileName.split('/')
  return parts.length > 1 ? parts[parts.length - 1] : fileName
}

/**
 * Validates and cleans social media input for Instagram and TikTok.
 * The function handles both URL formats (e.g., instagram.com/username) and handle formats (e.g., @username).
 *
 * @param input - The input string to validate and clean
 * @param platform - The social media platform ('instagram' or 'tiktok')
 * @returns An object containing:
 *   - isValid: boolean indicating if the input is valid
 *   - error?: string containing the error message if input is invalid
 *   - cleanedValue?: string containing the cleaned and validated value
 *
 * @example
 * // Valid Instagram URL
 * validateAndCleanSocialMediaInput('instagram.com/username', 'instagram')
 * // Returns: { isValid: true, cleanedValue: 'https://instagram.com/username' }
 *
 * @example
 * // Valid Instagram handle
 * validateAndCleanSocialMediaInput('@username', 'instagram')
 * // Returns: { isValid: true, cleanedValue: 'username' }
 *
 * @example
 * // Invalid input
 * validateAndCleanSocialMediaInput('invalid@email.com', 'instagram')
 * // Returns: { isValid: false, error: 'Please enter either a valid Instagram URL...' }
 */
export const validateAndCleanSocialMediaInput = (
  input: string,
  platform: 'instagram' | 'tiktok',
): { isValid: boolean; error?: string; cleanedValue?: string | null } => {
  if (!input) {
    return { isValid: true, cleanedValue: null }
  }

  const trimmedInput = input.trim()
  if (!trimmedInput) {
    return { isValid: true, cleanedValue: null }
  }

  // Remove @ if present at start
  const value = trimmedInput.startsWith('@')
    ? trimmedInput.substring(1)
    : trimmedInput

  const platformConfig = {
    instagram: {
      domain: 'instagram.com',
      example: 'instagram.com/username',
    },
    tiktok: {
      domain: 'tiktok.com',
      example: 'tiktok.com/@username',
    },
  }

  const config = platformConfig[platform]

  // Check if it's a URL
  if (value.includes(`${config.domain}/`)) {
    try {
      const cleanedUrl = cleanUrl(value)
      const url = new URL(cleanedUrl)
      const pathParts = url.pathname.split('/').filter(Boolean)

      // If it's a post/design URL (contains additional paths), accept it as is
      if (pathParts.length > 1) {
        return { isValid: true, cleanedValue: cleanedUrl }
      }

      // For profile URLs, validate the handle
      const handle = pathParts[0]?.replace(/^@/, '')
      if (!handle) {
        return {
          isValid: false,
          error: `Please enter a valid ${platform} URL with username (e.g., ${config.example})`,
        }
      }

      if (!/^[a-zA-Z0-9._]+$/.test(handle)) {
        return {
          isValid: false,
          error: `Invalid ${platform} profile URL format`,
        }
      }

      return { isValid: true, cleanedValue: cleanedUrl }
    } catch {
      return {
        isValid: false,
        error: `Please enter a valid ${platform} URL with username (e.g., ${config.example})`,
      }
    }
  }

  // If not URL, validate as handle
  if (!/^[a-zA-Z0-9._]+$/.test(value)) {
    return {
      isValid: false,
      error: `Please enter either a valid ${platform} URL with username (e.g., ${config.example}) or a valid username (letters, numbers, periods and underscores only)`,
    }
  }

  // Additional check to prevent common mistakes
  if (value.includes('.') && !value.includes(`${config.domain}/`)) {
    return {
      isValid: false,
      error: `Please enter either a valid ${platform} URL with username (e.g., ${config.example}) or just the username without domain`,
    }
  }

  return { isValid: true, cleanedValue: value }
}

/**
 * Validates and cleans other social media links.
 * The function ensures the input is a valid URL and adds the https:// protocol if missing.
 *
 * @param input - The input string to validate and clean
 * @returns An object containing:
 *   - isValid: boolean indicating if the input is valid
 *   - error?: string containing the error message if input is invalid
 *   - cleanedValue?: string containing the cleaned and validated URL
 *
 * @example
 * // Valid URL
 * validateAndCleanOtherSocialMediaLink('example.com')
 * // Returns: { isValid: true, cleanedValue: 'https://example.com' }
 *
 * @example
 * // Valid URL with protocol
 * validateAndCleanOtherSocialMediaLink('https://example.com')
 * // Returns: { isValid: true, cleanedValue: 'https://example.com' }
 *
 * @example
 * // Invalid input
 * validateAndCleanOtherSocialMediaLink('not a url')
 * // Returns: { isValid: false, error: 'Please enter a valid URL...' }
 */
export const validateAndCleanOtherSocialMediaLink = (
  input: string,
): { isValid: boolean; error?: string; cleanedValue?: string | null } => {
  if (!input) {
    return { isValid: true, cleanedValue: null }
  }

  const trimmedInput = input.trim()
  if (!trimmedInput) {
    return { isValid: true, cleanedValue: null }
  }

  // Remove @ if user accidentally added it
  const value = trimmedInput.startsWith('@')
    ? trimmedInput.substring(1)
    : trimmedInput

  // Check if input contains at least one dot (for domains)
  if (!value.includes('.')) {
    return {
      isValid: false,
      error:
        'Please enter a valid URL with a domain (e.g., https://www.example.com)',
    }
  }

  try {
    // Try to create a valid URL
    const url = new URL(value.startsWith('http') ? value : `https://${value}`)

    // Check if hostname is valid and contains at least one dot
    if (!url.hostname || !url.hostname.includes('.')) {
      return {
        isValid: false,
        error:
          'Please enter a valid URL with a domain (e.g., https://www.example.com)',
      }
    }

    // Check if hostname has a valid TLD (at least 2 characters after the last dot)
    const tld = url.hostname.split('.').pop()
    if (!tld || tld.length < 2) {
      return {
        isValid: false,
        error:
          'Please enter a valid URL with a domain (e.g., https://www.example.com)',
      }
    }

    // Check if the URL is functional by ensuring it has a valid structure
    if (!url.protocol || !url.hostname || url.hostname.length < 3) {
      return {
        isValid: false,
        error:
          'Please enter a valid URL with a domain (e.g., https://www.example.com)',
      }
    }

    // If all checks passed, clean the URL
    const cleanedUrl = cleanUrl(value)
    return { isValid: true, cleanedValue: cleanedUrl }
  } catch {
    return {
      isValid: false,
      error: 'Please enter a valid URL (e.g., https://www.example.com)',
    }
  }
}
